<template>
  <div class="workflow-statistics">
    <!-- Header -->
    <div class="statistics-header">
      <h4>Workflow Trends</h4>
      <!-- Period Selector -->
      <div class="period-tabs" v-if="showPeriodSelector">
        <button 
          v-for="days in periods" 
          :key="days"
          @click="changePeriod(days)"
          :class="['period-tab', { active: internalPeriod === days }]"
        >
          {{ days === 1 ? '24h' : days + 'd' }}
        </button>
      </div>
    </div>

    <!-- KPI Cards -->
    <div class="kpi-cards">
      <div class="kpi-card">
        <div class="kpi-icon">
          <i class="bi bi-bar-chart-fill"></i>
        </div>
        <div class="kpi-details">
          <div class="kpi-label">Total Executions</div>
          <div class="kpi-value">{{ stats.total_executions || 0 }}</div>
        </div>
      </div>
      
      <div class="kpi-card success">
        <div class="kpi-icon">
          <i class="bi bi-check-circle-fill"></i>
        </div>
        <div class="kpi-details">
          <div class="kpi-label">Success Rate</div>
          <div class="kpi-value">{{ successRate }}%</div>
        </div>
      </div>
      
      <div class="kpi-card">
        <div class="kpi-icon">
          <i class="bi bi-stopwatch-fill"></i>
        </div>
        <div class="kpi-details">
          <div class="kpi-label">Avg Duration</div>
          <div class="kpi-value">{{ formattedDuration }}</div>
        </div>
      </div>
      
      <div class="kpi-card failure">
        <div class="kpi-icon">
          <i class="bi bi-x-circle-fill"></i>
        </div>
        <div class="kpi-details">
          <div class="kpi-label">Failure Rate</div>
          <div class="kpi-value">{{ failureRate }}%</div>
        </div>
      </div>
    </div>

    <!-- Time Series Graph -->
    <WorkflowTimeSeriesChart 
      v-if="timeSeriesData && timeSeriesData.length > 0"
      :timeSeriesData="timeSeriesData"
      :title="chartTitle"
    />
  </div>
</template>

<script>
import WorkflowHistory from '@/lib/WorkflowHistory';
import WorkflowTimeSeriesChart from './WorkflowTimeSeriesChart.vue';
import { formatDuration } from '@/shared/workflowHistoryUtils';

export default {
  name: 'WorkflowStatistics',
  components: {
    WorkflowTimeSeriesChart
  },
  props: {
    selectedPeriod: {
      type: Number,
      default: 7
    },
    periods: {
      type: Array,
      default: () => [7, 14, 30, 90]
    },
    showPeriodSelector: {
      type: Boolean,
      default: true
    },
    chartTitle: {
      type: String,
      default: 'Execution Trends'
    }
  },
  data() {
    return {
      stats: {
        total_executions: 0,
        status_counts: {
          completed: 0,
          failed: 0,
          timeout: 0
        },
        overall_avg_duration: 0
      },
      timeSeriesData: [],
      loading: false,
      workflowHistoryService: null,
      internalPeriod: this.selectedPeriod
    };
  },
  computed: {
    successRate() {
      if (!this.stats || !this.stats.total_executions) return 0;
      const completed = this.stats.status_counts && this.stats.status_counts.completed || 0;
      return Math.round((completed / this.stats.total_executions) * 100);
    },
    failureRate() {
      if (!this.stats || !this.stats.total_executions) return 0;
      const errored = this.stats.status_counts && this.stats.status_counts.errored || 0;
      const killed = this.stats.status_counts && this.stats.status_counts.killed || 0;
      const timeout = this.stats.status_counts && this.stats.status_counts.timeout || 0;
      const failed = errored + killed + timeout;
      return Math.round((failed / this.stats.total_executions) * 100);
    },
    formattedDuration() {
      return formatDuration(this.stats.overall_avg_duration || 0);
    }
  },
  watch: {
    selectedPeriod(newVal) {
      this.internalPeriod = newVal;
      this.loadData();
    }
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const statsData = await this.workflowHistoryService.fetchStats(this.internalPeriod);
        this.stats = statsData;
        this.generateTimeSeriesData(statsData.daily_counts || []);
      } catch (error) {
        console.error('Error loading workflow statistics:', error);
      } finally {
        this.loading = false;
      }
    },
    
    generateTimeSeriesData(dailyCounts) {
      const now = new Date();
      const dayMap = new Map();

      for (let i = this.internalPeriod - 1; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        date.setHours(0, 0, 0, 0);
        const dateKey = date.toISOString().split('T')[0];
        dayMap.set(dateKey, { completed: 0, errored: 0, timeout: 0, total: 0 });
      }

      dailyCounts.forEach(entry => {
        if (dayMap.has(entry.date)) {
          dayMap.set(entry.date, {
            completed: entry.completed || 0,
            errored: entry.errored || 0,
            timeout: entry.timeout || 0,
            total: entry.total || 0,
          });
        }
      });

      this.timeSeriesData = Array.from(dayMap.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([date, data]) => ({
          date,
          ...data
        }));
    },
    
    changePeriod(days) {
      this.internalPeriod = days;
      this.$emit('change-period', days);
      this.loadData();
    }
  },
  mounted() {
    this.workflowHistoryService = new WorkflowHistory(this.$workflowApi);
    this.loadData();
  }
};
</script>

<style scoped>
.workflow-statistics {
  width: 100%;
}

.statistics-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.statistics-header h4 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.period-tabs {
  display: flex;
  gap: 8px;
  background-color: var(--form-bg);
  padding: 4px;
  border-radius: 8px;
  border: 1px solid var(--border-default);
}

.period-tab {
  padding: 8px 16px;
  border: 1px solid var(--border-default);
  background: var(--form-bg);
  color: #9ca3af;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s ease;
}

.period-tab:hover {
  background: var(--bg-card);
  border-color: var(--border-hover);
  color: var(--text-primary);
}

.period-tab.active {
  background: var(--primary-start);
  border-color: var(--primary-start);
  color: var(--text-primary);
}

.kpi-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.kpi-card {
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 8px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: border-color 0.2s ease;
}

.kpi-card:hover {
  border-color: var(--border-hover);
}

.kpi-card.success {
  border-left: 3px solid #10b981;
}

.kpi-card.failure {
  border-left: 3px solid #ef4444;
}

.kpi-icon {
  font-size: 32px;
  opacity: 0.8;
}

.kpi-details {
  flex: 1;
}

.kpi-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #9ca3af;
  font-weight: 600;
  margin-bottom: 4px;
}

.kpi-value {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
}
</style>
